<!-- Copyright: Seán I. O'Donoghue -->
<!-- TODO: styling for hover on dropdowns -->

<template>
  <!-- hasAccessFilter: on phones, the unchosen Session menu steps aside (CSS below) -->
  <!-- Show All first, left of the menus (2026-10-21, Seán) -->
  <PageHeader
    title="Videos"
    to="/Videos"
    :count="videoCount"
    :total="videoTotal"
    :controls-class="{hasAccessFilter: accessCategory, isVideoLocked}">
    <ButtonBespoke v-if="year || session || accessCategory" id="showAll" @click="showAll">
      Show All
    </ButtonBespoke>
    <Dropdown
      v-if="showYear"
      placeholder="Year"
      :tag="$route.params.vimeoHash ? false : true"
      :options="years"
      :selected="year"
      path="/Videos" />
    <Dropdown
      v-if="showSession"
      :class="{isUnchosen: !session}"
      placeholder="Session"
      :tag="$route.params.vimeoHash ? false : true"
      :options="sessions"
      :selected="session"
      :labels="sessionLabels"
      path="/Videos" />
  </PageHeader>
  <div class="view-wrapper" :class="{hasActiveFilters}">
    <VideoAccessInfoButton
      v-if="!$route.params.vimeoHash && (isUnlockActive || accessState !== 'full')"
      :label="unlockLabel"
      :is-prominent="!isUnlockActive"
      @click="isUnlockActive ? $router.push('/Videos') : $router.push('/Videos/Unlock')" />
    <AccessCategories
      v-if="!$route.params.vimeoHash && !isUnlockActive"
      :showcases="showcases"
      :selected="accessCategory"
      @select="filterByAccess" />
    <AccessMessage
      v-if="!$route.params.vimeoHash && !isUnlockActive"
      :selected="accessCategory" />
    <div class="router-view-wrapper">
      <router-view v-slot="{Component}">
        <component
          :is="Component"
          :options="options"
          @filterByYear="filterByYear"
          @filterBySession="filterBySession" />
      </router-view>
    </div>
  </div>
</template>

<script>
import {inject, ref, onMounted} from "vue";
import ButtonBespoke from "@/components/ButtonBespoke";
import PageHeader from "@/components/PageHeader.vue";
import Dropdown from "@/components/DropdownRoutes/Dropdown";
import VideoAccessInfoButton from "@/components/Video/VideoAccessInfoButton.vue";
import AccessCategories from "@/components/Video/AccessCategories.vue";
import AccessMessage from "@/components/Video/AccessMessage.vue";
import useVideoAccess from "@/composables/useVideoAccess";
import useUserSafe from "@/composables/useUserSafe";
import {getIsUnlocked, getShowcasesInCategory} from "@/components/Video/videoCards";
import {getSpeakerKey, getVideoSpeakerKeys} from "@/composables/useSpeakerIndex";
const reactiveDisplayWidth = ref(null);
// the Session menu (2026-10-21, Seán): Keynotes, Masterclass and the showcases, then VIZBI's
// six sessions in their order, then the rest; a line between the groups. Sessions not listed go
// last.
const SESSION_GROUPS = [
  ["Keynote", "Masterclass", "Showcase", "Shorts"],
  ["DNA", "RNA", "Proteins", "Cells", "Tissues", "Ecosystems"],
  ["Tech-Track", "Commentary"]
];
const SESSION_LABELS = {
  Keynote: "Keynotes",
  Masterclass: "Masterclasses",
  Showcase: "Showcases",
  "Tech-Track": "Tech-Tracks"
};
// one menu entry for several Vimeo tags (/Videos/Commentary)
const SESSION_MERGES = {Commentary: ["Opening", "Closing"]};

function getMergedSession(tag) {
  const merged = Object.keys(SESSION_MERGES).find((name) => SESSION_MERGES[name].includes(tag));
  return merged || tag;
}

// "VIZBI 2013", "VIZBI Showcase 2013", "Masterclass 2026" → its year
const getShowcaseYear = (name) => name.match(/\d{4}$/)?.[0] || name;

const getSessionTags = (session) => SESSION_MERGES[session] || [session];

// a session as the menu spells it, whatever the URL's case ("masterclass" → "Masterclass")
function getSessionSpelling(sessions, name) {
  const lower = String(name).toLowerCase();
  const names = [...sessions, ...Object.keys(SESSION_MERGES)].filter(Boolean);
  return names.find((session) => session.toLowerCase() === lower) || name;
}

// the sessions in their groups, null between groups (DropdownMenu draws a line)
function getSessionOptions(tags) {
  const sessions = [...new Set(tags.map(getMergedSession))];
  const known = SESSION_GROUPS.flat();
  const groups = SESSION_GROUPS.map((group) => group.filter((name) => sessions.includes(name)));
  groups[groups.length - 1].push(...sessions.filter((name) => !known.includes(name)));
  const filled = groups.filter((group) => group.length);
  return filled.flatMap((group, index) => (index ? [null, ...group] : group));
}
export default {
  components: {
    ButtonBespoke,
    PageHeader,
    Dropdown,
    VideoAccessInfoButton,
    AccessCategories,
    AccessMessage
  },
  setup() {
    const displayWidth = inject("displayWidth");
    const {accessState} = useVideoAccess();
    const {user, isSignedIn} = useUserSafe();
    onMounted(() => {
      reactiveDisplayWidth.value = displayWidth.value;
      console.log(`displayWidth = ${reactiveDisplayWidth.value}`);
    });
    const sessionLabels = SESSION_LABELS;
    return {reactiveDisplayWidth, accessState, user, isSignedIn, sessionLabels};
  },
  data() {
    return {
      got_showcase: {}, // remember video showcase
      got_privacy: {}, // remember video privacy
      thumbnails: {},
      registered_events: {},
      showcases: {}, // loaded from API in mounted()
      showYear: true,
      showSession: true,
      session: this.$route.params.session || "", // empty string = all session
      year: this.$route.params.year || "" // .toString() - empty string = all years
    };
  },
  computed: {
    isUnlockActive() {
      return String(this.$route.name).startsWith("Videos_Unlock"); // Unlock and its Masterclass page
    },
    // partial access (one collection of two): the categories below show what's locked, so the
    // bar just says how (2026-10-21, Seán)
    unlockLabel() {
      if (this.isUnlockActive) return "Return to VIZBI Video Library";
      if (this.accessState === "none") return "How do I unlock all videos?";
      return "How to unlock";
    },
    options() {
      return {
        get_showcase: this.get_showcase,
        get_privacy: this.get_privacy,
        showcases: this.showcases,
        thumbnails: this.thumbnails,
        filteredShowcases: this.filteredShowcases,
        year: this.year,
        session: this.session
      };
    },
    dev_server() {
      if (window.location.host.match(/^dev.vizbi.org/)) {
        // on dev.vizbi.org, show staged videos
        return true;
      } else {
        return false;
      }
    },
    localhost() {
      if (window.location.host.match(/^localhost|^192/)) {
        // on localhost, show the very latest Showcase
        return true;
      } else {
        return false;
      }
    },
    years() {
      let years = []; // 'All'
      Object.keys(this.showcases).forEach(function (year) {
        // Accumulate list of years
        year = getShowcaseYear(year);
        if (!years.includes(year)) {
          years.push(year);
        }
      });
      // newest first, whatever order Vimeo's showcases were made in
      return years.sort((a, b) => b - a);
    },
    sessions() {
      let sessions = []; // 'All'
      var _this = this;
      Object.keys(this.showcases).forEach(function (year) {
        // Accumulate list of sessions
        Object.keys(_this.showcases[year].videos).forEach(function (hash) {
          let session = _this.showcases[year].videos[hash].tag;
          if (session && !sessions.includes(session) && !/\s/.test(session)) {
            sessions.push(session);
          }
        });
      });
      return getSessionOptions(sessions);
    },
    totalVideos() {
      return this.countVideosIn(this.showcases);
    },
    // the header's count: 1 of all on a video's page, the filtered ones of all, or all
    videoCount() {
      if (!this.totalVideos) return "";
      if (this.$route.params.vimeoHash) return 1;
      return this.filteredVideos || this.totalVideos;
    },
    // the whole library beside one video or a filtered list ("[47/1,076]")
    videoTotal() {
      const isPart = this.$route.params.vimeoHash || this.filteredVideos;
      return this.totalVideos && isPart ? this.totalVideos : "";
    },
    speakerKey() {
      const speaker = this.$route.params.speaker;
      return speaker ? getSpeakerKey(speaker.replaceAll("_", " ")) : "";
    },
    filteredShowcases() {
      if (this.$route.params.vimeoHash) return {};
      if (this.speakerKey) return this.getSpeakerShowcases(this.speakerKey);
      this.log(`filterShowcases(${this.session}, ${this.year})`);
      var _this = this;
      var filteredShowcases = {};
      var value;
      if (this.session) {
        // Retrieve list of session tags for the dropdown menu
        Object.keys(_this.showcases).forEach(function (year) {
          Object.keys(_this.showcases[year].videos).forEach(function (hash) {
            value = _this.showcases[year].videos[hash].tag;
            if (getSessionTags(_this.session).includes(value)) {
              if (!filteredShowcases[year]) {
                filteredShowcases[year] = {};
                filteredShowcases[year].videos = {};
              }
              filteredShowcases[year].videos[hash] = _this.showcases[year].videos[hash];
            }
          });
        });
      } else if (this.year) {
        Object.keys(_this.showcases).forEach(function (item) {
          value = getShowcaseYear(item);
          if (value === _this.year) {
            filteredShowcases[item] = _this.showcases[item];
          }
        });
      } else {
        filteredShowcases = this.showcases;
      }
      this.log(`filteredShowcases = ${Object.keys(filteredShowcases).length}`);
      if (!this.accessCategory) return filteredShowcases;
      const viewer = {user: this.user, isSignedIn: this.isSignedIn};
      return getShowcasesInCategory(filteredShowcases, this.accessCategory, viewer);
    },
    // any filter on (a year, a session, a speaker, an access category): its control turns green and
    // the orange steps back (2026-10-21, Seán)
    hasActiveFilters() {
      // a video page: its year and session show where it belongs, they aren't filters
      if (this.$route.params.vimeoHash) return false;
      return Boolean(this.year || this.session || this.speakerKey || this.accessCategory);
    },
    // a video page this viewer can't play yet (Video/Bespoke.vue shows its unlock bar): signed out
    // on a Free video, or a Restricted one without access; as the cards' padlocks decide
    isVideoLocked() {
      const hash = this.$route.params.vimeoHash;
      const showcase = hash && Object.keys(this.showcases).find((name) => {
        return this.showcases[name].videos?.[hash];
      });
      if (!showcase) return false;
      const video = this.showcases[showcase].videos[hash];
      const viewer = {user: this.user, isSignedIn: this.isSignedIn};
      return !getIsUnlocked({video, hash, showcase, ...viewer});
    },
    // an access category shown alone (AccessCategories.vue: /Videos?unlocked or ?locked; the
    // router keeps it to one of them)
    accessCategory() {
      const {query} = this.$route;
      if ("locked" in query) return "locked";
      return "unlocked" in query ? "unlocked" : "";
    },
    filteredVideos() {
      let result = this.countVideosIn(this.filteredShowcases);
      if (result !== this.totalVideos) {
        return result;
      } else {
        return 0; // computed can't return 'undefined' -
      }
    }
  },
  async mounted() {
    this.log("Videos mounted()");
    this.showcases = await fetch("/sitemaps/videos/showcases-lite.json")
      .then((r) => r.ok ? r.json() : {}).catch(() => ({}));
    if (this.$route.params.vimeoHash) {
      await this.get_showcase(this.$route.params.vimeoHash); // don't need?
      this.log(`reactiveDisplayWidth.value = ${reactiveDisplayWidth.value}`);
      if (reactiveDisplayWidth.value === "narrow") {
        this.log(`displayWidth is narrow`);
        [this.showYear, this.showSession] = [false, false];
      }
      return;
    }
    if (this.$route.params.year) {
      [this.showYear, this.showSession] = [true, false];
      return;
    }
    if (this.speakerKey) return this.showSpeakerVideos();
    if (!this.$route.params.session) return;
    // the router matches /Videos/masterclass too: shown under its own spelling, /Videos/Masterclass
    const spelling = getSessionSpelling(this.sessions, this.$route.params.session);
    if (spelling !== this.$route.params.session) {
      this.session = spelling;
      this.$router.replace(`/Videos/${spelling}`);
    }
    if (!this.sessions.includes(spelling)) {
      this.log(`URL has invalid session name`);
      this.showAll();
      return;
    }
    [this.showYear, this.showSession] = [false, true];
  },
  methods: {
    // the showcases, holding only the videos by this speaker (getVideoSpeakerKeys)
    getSpeakerShowcases(speakerKey) {
      const selected = {};
      for (const [name, showcase] of Object.entries(this.showcases)) {
        for (const [hash, video] of Object.entries(showcase.videos || {})) {
          if (!getVideoSpeakerKeys(video.name).includes(speakerKey)) continue;
          if (!selected[name]) selected[name] = {...showcase, videos: {}};
          selected[name].videos[hash] = video;
        }
      }
      return selected;
    },
    // /Videos/David_Goodsell: one video → its page; none → the whole library
    showSpeakerVideos() {
      const hashes = Object.values(this.filteredShowcases).flatMap((showcase) => {
        return Object.keys(showcase.videos);
      });
      if (hashes.length === 1) return this.$router.replace(`/Videos/${hashes[0]}`);
      if (!hashes.length) return this.$router.replace("/Videos");
      [this.showYear, this.showSession] = [false, false];
    },
    // eslint-disable-next-line
    log(message) {
      process.env.VUE_APP_DEV && console.log(message); // eslint-disable-line
    },
    get_privacy(vimeoHash, showcase) {
      if (this.got_privacy[vimeoHash]) {
        return this.got_privacy[vimeoHash]; // if privacy is already known
      }
      if (!showcase) {
        // if called without defining showcase, look it up
        showcase = this.get_showcase(vimeoHash);
      }
      let privacy;
      if (!showcase || !this.showcases[showcase]) {
        privacy = false; // unlisted video, so privacy is unknown
      } else if (!this.showcases[showcase]["videos"][vimeoHash]) {
        privacy = false; // privacy is unknown
      } else if (!this.showcases[showcase]["videos"][vimeoHash]["privacy"]) {
        privacy = false; // privacy is unknown
      } else {
        privacy = this.showcases[showcase]["videos"][vimeoHash]["privacy"] !== "anybody";
      }
      this.got_privacy[vimeoHash] = privacy; // remember privacy of this video
      return privacy;
    },
    filterByYear(year) {
      this.log(`filterByYear: '${year}'  (called from Tag)`);
      if (year === "All" || year === "") return this.showAll();
      this.$router.push(`/Videos/${year}`);
      // if (!this.$route.params.vimeoHash) {
      //   this.year = year;
      //   [this.showYear, this.showSession] = [true, false];
      //   this.session = "";
      // }
    },
    filterBySession(session) {
      this.log(`filterBySession: '${session}' (called from Tag)`);
      if (session === "All" || session === "") return this.showAll();
      this.$router.push(`/Videos/${getMergedSession(session)}`);
      // this.session = session;
      // [this.showYear, this.showSession] = [false, true];
      // this.year = "";
    },
    // one filter at a time (2026-10-21, Seán): an access category replaces a year, session or
    // speaker (and a menu's year or session, a link without the query, replaces it)
    filterByAccess(category) {
      const query = category ? {[category]: null} : {};
      this.$router.push({path: "/Videos", query});
    },
    showAll() {
      this.log(`Show all years and sessions`);
      this.$router.push(`/Videos`);
      // this.year = "";
      // this.session = "";
      // [this.showYear, this.showSession] = [true, true];
      // if (this.$route.params.vimeoHash) return this.$router.push(`/Videos`);
      // history.pushState({}, null, `/Videos`); // silent
    },
    get_showcase(vimeoHash) {
      this.log(`get_showcase(${vimeoHash})`);
      let showcase; // local variable
      if (!vimeoHash) {
        console.warn(`vimeoHash not found`);
        return;
      }
      if (!this.showcases) {
        console.warn(`this.showcases not found`);
        return;
      }
      if (Object.keys(this.showcases).length === 0) return; // not loaded yet
      if (this.got_showcase[vimeoHash]) {
        // if already found, use previous value
        return this.got_showcase[vimeoHash];
      }
      this.log(`About to check ${Object.keys(this.showcases).length} filteredShowcases`);
      for (showcase in this.showcases) {
        this.log(`Checking showcase: ${showcase}`);
        if (this.showcases[showcase]["videos"][vimeoHash]) {
          this.log(`Found showcase = ${showcase}`);
          let match = / (\d+)$/.exec(showcase);
          if (match[1]) {
            this.year = match[1];
            this.log(`Set year: "${this.year}"`);
          }
          this.session = getMergedSession(this.showcases[showcase]["videos"][vimeoHash]["tag"]);
          this.log(`Set session: "${this.session}"`);
          this.thumbnails = this.showcases[showcase]["videos"][vimeoHash]["pictures"];
          break; // end for loop
        } // otherwise, keep iterating over filteredShowcases
      }
      //return this.showcase;
      // the loop leaves the last showcase in showcase when none holds the video
      if (!showcase || !this.showcases[showcase]["videos"][vimeoHash]) {
        console.warn(`Missing needed meta data; returning to /Videos page`);
        showcase = ""; // for unlisted videos
        history.pushState({}, null, `/Videos`); // silent
      }
      this.got_showcase[vimeoHash] = showcase; // remember video showcase
      return;
    },
    countVideosIn(showcases) {
      // count total number of videos in a group of showcases
      let result = 0;
      for (let showcase of Object.values(showcases)) {
        // this.log(`result = ${result}`);
        // this.log(`showcase = ${Object.keys(showcase.videos).length}`);
        result += Object.keys(showcase.videos || {}).length;
        // this.log(`result = ${result}`);
      }
      return result;
    }
  }
};
</script>

<style scoped>
/* phones, an access category chosen (2026-10-21, Seán): Show All and Year, no Session menu
   (three don't fit beside the heading), unless it's a chosen filter (its green tag stays) */
@media (max-width: 560px) {
  #right.hasAccessFilter .isUnchosen {
    display: none;
  }
}
/* .narrow #showAll {
display: none;
} */
a {
  margin-right: 20px;
}
/* room under the heading's row, so it doesn't read as one block with the buttons below
   (2026-10-21, Seán) */
.view-wrapper {
  position: relative;
  margin-top: 13px;
}
.router-view-wrapper {
  margin-top: 0;
}
/* one family of filter controls (2026-10-21, Seán): Year, Session and the access categories
   (AccessCategories.vue) neutral and the same height, chosen ones green (a tag, or aria-pressed).
   The unlock bar is outlined: orange while nothing is filtered, neutral grey with any filter on;
   only its text and border change. The colours, for every control here: */
/* the access categories' and unlock bar's colours (the header's filters: PageHeader.vue) */
.view-wrapper {
  --filter-height: 2.5rem;
  --filter-weight: 600; /* the controls' labels: a little bold (2026-10-21, Seán) */
  --filter-text: var(--filter-control-text);
  --filter-border: var(--filter-control-border);
  --filter-background: var(--filter-control-background);
  --filter-muted: color-mix(in srgb, var(--text-color) 60%, var(--background-color));
  /* the unlock bar in the brand colour (global.css --unlock-*): light mode a dark brand fill,
     dark mode an outline; the chosen filters are the plain brand fill */
  --unlock-strong: color-mix(in srgb, black var(--unlock-darken), var(--brand-color));
  --unlock-color: var(--unlock-strong);
  --unlock-label: color-mix(
    in srgb, var(--brand-color) var(--unlock-text-share), var(--unlock-text-base, black)
  );
  --unlock-background: color-mix(
    in srgb, var(--unlock-strong) var(--unlock-fill-share), transparent
  );
}
.view-wrapper.hasActiveFilters {
  --unlock-color: var(--filter-muted);
  --unlock-label: var(--filter-muted);
  --unlock-background: transparent;
}
.view-wrapper :deep(.access-info-btn.isProminent) {
  min-height: var(--filter-height);
  color: var(--unlock-label);
  background: var(--unlock-background);
  border: 1px solid var(--unlock-color);
}
.view-wrapper :deep(.access-info-btn.isProminent) {
  font-weight: 700;
}
.view-wrapper :deep(.access-info-btn.isProminent:hover) {
  background: var(--unlock-background);
  filter: brightness(1.08);
}
/* the Unlock page's "Return to VIZBI Video Library": the brand colour, as the unlock bar */
.view-wrapper :deep(.access-info-btn:not(.isProminent)) {
  color: var(--brand-color);
  border-color: color-mix(in srgb, var(--brand-color) 45%, transparent); /* a light green edge */
}
/* a locked video's Purchase access and Sign in (Video/Bespoke.vue): not filter controls, so their
   own square look, subordinate to the unlock bar above but in its colour (global.css
   --second-*: light mode, dark brand text on a pale brand fill; dark mode, light brand text on
   dark grey), 2026-10-21, Seán */
.view-wrapper :deep(.access-inline-buttons .access-action-btn) {
  color: color-mix(
    in srgb,
    white var(--second-text-lift),
    color-mix(in srgb, black var(--second-text-darken), var(--brand-color))
  );
  background: color-mix(in srgb, var(--brand-color) var(--second-share), var(--second-base));
  border-color: color-mix(
    in srgb, var(--brand-color) var(--second-border-share), var(--second-border-base)
  );
}
.view-wrapper :deep(.access-inline-buttons .access-action-btn:hover) {
  filter: brightness(0.97);
}
/* Show All (shown only with a filter on; 2026-10-21, Seán): a chip like a chosen tag (height,
   radius, type, shadow) and the other controls' border, but outlined (an action, not a state):
   the page's background and text */
#right #showAll {
  min-width: 0;
  min-height: var(--filter-height);
  padding: 0 0.9rem;
  font-weight: var(--filter-weight);
  color: var(--brand-hover-color); /* green text and border: secondary attention to the way out of
    the chosen filter, an outline, not the chosen green fill (2026-10-21, Seán) */
  background: var(--background-color);
  border: 1px solid var(--brand-color);
  border-radius: 0.5rem;
  box-shadow: 0 0.13rem 0.13rem rgba(0, 0, 0, 0.2);
  text-decoration: none;
}
/* a locked video: the unlock bar is the one thing to steer to, so Show All loses its green */
#right.isVideoLocked #showAll {
  color: var(--text-color);
  border-color: var(--filter-border);
}
#right #showAll:hover {
  background: var(--background-color-subtle);
}
#right #showAll:active {
  opacity: 0.85;
}
</style>
